import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../store/slices/affiliatesReducer";
import { useTranslation } from "../hooks/useTranslation";
import LanguageSwitcher from "./LanguageSwitcher";
import ProfileModal from "./ProfileModal";
import WorkflowDrawer from "./navigation/WorkflowDrawer";
import { Menu as MenuIcon, X as CloseIcon } from "lucide-react";

const AppHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isItemWorkflowOpen, setIsItemWorkflowOpen] = useState(false);
  
  // Get user data from Redux state
  const user = useSelector((state) => state.affiliate.auth) || { name: "Test Affiliate", role: "affiliate" };
  const isDietitian = user?.affiliate_type === 'dietitian';

  // Helper function to get user initials safely
  const getUserInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    
    const trimmedName = name.trim();
    if (trimmedName.length === 0) return '?';
    
    // Handle multiple names (first letter of first and last name)
    const names = trimmedName.split(' ').filter(n => n.length > 0);
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    
    return trimmedName[0].toUpperCase();
  };

  // Helper function to get user display name safely
  const getUserDisplayName = (name) => {
    if (!name || typeof name !== 'string') return 'User';
    return name.trim() || 'User';
  };

  // Navigation items - different for dietitians
  const navigation = isDietitian ? [
    { name: t("Dashboard") || "Dashboard", href: "/", current: location.pathname === "/" && !location.pathname.startsWith("/kostdata") },
    { name: t("Patientprofiler") || "Patientprofiler", href: "/kostdata/patients", current: location.pathname.startsWith("/kostdata/patients") },
    { name: t("Måltider") || "Måltider", href: "/kostdata/meals", current: location.pathname.startsWith("/kostdata/meals") },
    { name: t("Receptkategorier") || "Receptkategorier", href: "/categories", current: location.pathname.startsWith("/categories") || location.pathname.startsWith("/items/") },
  ] : [
    { name: "Dashboard", href: "/", current: location.pathname === "/" },
    { name: "Menyer", href: "/menyer", current: location.pathname === "/menyer" },
    { name: "Recept", href: "/", current: location.pathname === "/" },
    { name: "Hållbarhet", href: "/reports", current: location.pathname.startsWith("/reports") },
  ];

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-[1200] bg-gray-100/95 backdrop-blur shadow-none border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 tablet:px-6 laptop:px-8 desktop:px-8">
        <div className="flex justify-between items-center h-20 mobile:h-20 tablet:h-24 laptop:h-24 desktop:h-24">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link to={isDietitian ? "/kostdata/patients" : "/"} className="group flex items-center space-x-2 mobile:space-x-2 tablet:space-x-4 laptop:space-x-4 desktop:space-x-4">
                {/* Logo/Image Square */}
                <div className="w-8 h-8 mobile:w-10 mobile:h-10 tablet:w-12 tablet:h-12 laptop:w-12 laptop:h-12 desktop:w-12 desktop:h-12 bg-gradient-to-br from-secondary-green to-secondary-green/90 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <img src="/assets/icons/menuwise_logo_white.png" alt="Logo" className="w-4 h-4 mobile:w-5 mobile:h-5 tablet:w-7 tablet:h-7 laptop:w-7 laptop:h-7 desktop:w-7 desktop:h-7 text-white" />
                </div>
                <h1 className="text-xl mobile:text-2xl tablet:text-3xl laptop:text-3xl desktop:text-3xl font-bold text-primary-dark group-hover:text-secondary-green transition-colors duration-200">
                  Menuwiz
                </h1>
              </Link>
            </div>
            <nav className="hidden tablet:ml-8 tablet:flex tablet:space-x-6 laptop:ml-10 laptop:space-x-8 desktop:ml-10 desktop:space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    item.current
                      ? "border-secondary-green text-gray-900 bg-secondary-green/5"
                      : "border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900 hover:bg-gray-50"
                  } inline-flex items-center px-3 mobile:px-4 tablet:px-4 laptop:px-4 desktop:px-4 py-2 border-b-2 text-sm font-semibold rounded-lg transition-all duration-200`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-3 mobile:space-x-4 tablet:space-x-6 laptop:space-x-6 desktop:space-x-6">
            {/* Language Switcher */}
            <LanguageSwitcher className="hidden tablet:block laptop:block desktop:block" />

            {location.pathname.startsWith("/items/") && (
              <button
                type="button"
                onClick={() => setIsItemWorkflowOpen(true)}
                className="hidden mobile:hidden tablet:inline-flex laptop:inline-flex desktop:inline-flex items-center gap-2 rounded-lg border border-secondary-green/30 bg-secondary-green/10 px-3 py-2 text-xs font-semibold text-black transition hover:bg-secondary-green/20"
              >
                <img
                  src="/assets/icons/arrow_right.png"
                  alt="Arbetsflöde"
                  className="w-4 h-4"
                />
                {t("Arbetsflöde")}
              </button>
            )}

            {location.pathname === "/" && (
              <button
                type="button"
                onClick={() => setIsHelpOpen(true)}
                className="hidden tablet:inline-flex laptop:inline-flex desktop:inline-flex items-center gap-2 rounded-lg border border-secondary-green/30 bg-secondary-green/10 px-3 py-2 text-xs font-semibold text-secondary-green transition hover:bg-secondary-green/20"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-secondary-green text-[11px] font-bold text-white">
                  ?
                </span>
                {t("Hjälp")}
              </button>
            )}
            
            {/* User Info */}
            <div className="hidden tablet:flex laptop:flex desktop:flex items-center space-x-3 mobile:space-x-4 tablet:space-x-4 laptop:space-x-4 desktop:space-x-4">
              
              {/* Enhanced User Avatar Circle - Clickable */}
              <div className="relative group">
                <button
                  onClick={() => setIsProfileModalOpen(true)}
                  className="w-8 h-8 mobile:w-10 mobile:h-10 tablet:w-10 tablet:h-10 laptop:w-10 laptop:h-10 desktop:w-10 desktop:h-10 bg-gradient-to-br from-secondary-green/20 to-secondary-green/40 rounded-full flex items-center justify-center shadow-none border-2 border-secondary-green/30 hover:border-secondary-green/50 transition-all duration-200 hover:scale-105 cursor-pointer overflow-hidden"
                >
                  {user.image ? (
                    <img 
                      src={user.image} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-bold text-secondary-green leading-none tracking-wide">
                      {getUserInitials(user.name)}
                    </span>
                  )}
                </button>
                {/* Hover tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  {getUserDisplayName(user.name)} - Click to edit profile
                </div>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 mobile:py-2 tablet:py-2 laptop:py-2 desktop:py-2 px-3 mobile:px-4 tablet:px-4 laptop:px-4 desktop:px-4 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm"
            >
              <span className="hidden mobile:inline tablet:inline laptop:inline desktop:inline">{t('Logga ut')}</span>
              <span className="mobile:hidden tablet:hidden laptop:hidden desktop:hidden">{t('Logga ut').substring(0, 2)}</span>
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="tablet:hidden laptop:hidden desktop:hidden inline-flex items-center justify-center p-2 rounded-lg mobile:rounded-xl text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-secondary-green transition-all duration-200"
            >
              <span className="sr-only">Open main menu</span>
              <MenuIcon className={`${isMenuOpen ? "hidden" : "block"} h-5 w-5 mobile:h-6 mobile:w-6`} />
              <CloseIcon className={`${isMenuOpen ? "block" : "hidden"} h-5 w-5 mobile:h-6 mobile:w-6`} />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`${isMenuOpen ? "block" : "hidden"} tablet:hidden laptop:hidden desktop:hidden`}>
          <div className="pt-2 pb-3 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`${
                  item.current
                    ? "bg-secondary-green/10 border-secondary-green text-secondary-green"
                    : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900"
                } block pl-4 pr-4 py-3 border-l-4 text-base font-semibold rounded-r-lg transition-all duration-200`}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
          </div>
          <div className="pt-4 pb-3 border-t border-gray-100">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0">
                {/* Enhanced Mobile User Avatar Circle - Clickable */}
                <button
                  onClick={() => setIsProfileModalOpen(true)}
                  className="w-10 h-10 bg-gradient-to-br from-secondary-green/20 to-secondary-green/40 rounded-full flex items-center justify-center shadow-md border-2 border-secondary-green/30 hover:border-secondary-green/50 transition-all duration-200 hover:scale-105 cursor-pointer overflow-hidden"
                >
                  {user.image ? (
                    <img 
                      src={user.image} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-bold text-secondary-green leading-none tracking-wide">
                      {getUserInitials(user.name)}
                    </span>
                  )}
                </button>
              </div>
              <div className="ml-4">
                <p className="text-base font-semibold text-gray-900">{getUserDisplayName(user.name)}</p>
                <p className="text-sm text-gray-500 capitalize">{user.role || 'User'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Profile Modal */}
      <ProfileModal 
        isOpen={isProfileModalOpen} 
        onClose={() => setIsProfileModalOpen(false)} 
      />
      {location.pathname.startsWith("/items/") && (
        <WorkflowDrawer
          open={isItemWorkflowOpen}
          onClose={() => setIsItemWorkflowOpen(false)}
        />
      )}
      {location.pathname === "/" && (
        <WorkflowDrawer
          open={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          title={t('Så funkar resan i Menuwiz')}
          description={t('Använd dessa steg för att gå från översikt till färdig rätt.')}
          steps={[
            {
              id: 'overview',
              title: t('1. Översikt'),
              intro: t('Börja på översikten. Skapa eller välj en kategori att arbeta med.'),
              bullets: [
                t('Klicka på Lägg till kategori för att skapa en ny sektion i menyn.'),
                t('Ange ett tydligt namn, lägg till bild och skriv en kort beskrivning så teamet förstår innehållet.'),
              ],
              example: null,
            },
            {
              id: 'category',
              title: t('2. Hantera recept i kategorin'),
              intro: t('Öppna kategorin för att se alla recept, kopiera befintliga eller skapa nya.'),
              bullets: [
                t('Använd knappen Lägg till recept för att bygga en ny rätt från grunden.'),
                t('Behöver du en variant? Kopiera ett recept och justera ingredienserna.'),
              ],
              example: t('Exempel: Kopiera “Vegetarisk gryta” och anpassa ingredienserna efter dagens tema.'),
            },
            {
              id: 'item',
              title: t('3. Finjustera och publicera rätten'),
              intro: t('Inne i receptverktyget justerar du ingredienser, hållbarhet och rapporter.'),
              bullets: [
                t('Följ arbetsflödet i sidan för att se alla steg – ingredienser, hållbarhet och rapporter.'),
                t('Exportera PDF-rapporter och dela med teamet innan publicering.'),
              ],
              example: t('Exempel: Lägg till viktförlust, kontrollera CO₂e och skriv en kort presentation av rätten.'),
            },
          ]}
          footerButtons={[
            { type: 'button', label: t('Jag förstår'), onClick: () => setIsHelpOpen(false) },
          ]}
        />
      )}
    </header>
  );
};

export default AppHeader;
